import { Dispatch, SetStateAction, useState } from 'react';

import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  FetchDataResult,
  TablePagination,
  TableSort,
} from 'components/WrappedComponents/Table/types';
import { HookSearchTypeWithSort } from 'types/searchType';

import { PaginationKey, usePaginationContext } from '../PaginationStoreContext';
import { backAlertMessage } from '../utils/backAlertMessage';

export interface DashboardSearchQueryResult<
  DtoType,
  FilterType,
  SortType extends string | undefined,
> {
  query: UseQueryResult<FetchDataResult<DtoType>>;
  pagination: TablePagination;
  setPagination: Dispatch<SetStateAction<TablePagination>>;
  filter: Partial<FilterType>;
  setFilter: Dispatch<SetStateAction<Partial<FilterType>>>;
  sort: TableSort<SortType>;
  setSort: Dispatch<SetStateAction<TableSort<SortType>>>;
}

export function useDashboardSearchQuery<
  DtoType,
  FilterType,
  SortType extends string | undefined,
  SearchResultDto,
>(
  hookSearch: HookSearchTypeWithSort<
    DtoType,
    Partial<FilterType>,
    SortType,
    SearchResultDto
  >,
  queryKeyLabel: string,
  defaultFilter: Partial<FilterType>,
  defaultSort: TableSort<SortType>,
  keyPagination: PaginationKey,
): DashboardSearchQueryResult<DtoType, FilterType, SortType> {
  const [pagination, setPagination] = usePaginationContext(keyPagination);

  const [filter, setFilter] = useState<Partial<FilterType>>(defaultFilter);
  const [sort, setSort] = useState<TableSort<SortType>>(defaultSort);

  const [search, convertResultToFetchDataResult] = hookSearch(
    filter,
    sort,
    pagination,
  );

  const fetchData = async () => {
    const result = await search().catch((e: Response) => {
      void backAlertMessage(e);
      throw e;
    });
    return convertResultToFetchDataResult(result);
  };

  const query = useQuery({
    queryKey: [queryKeyLabel, filter, sort, pagination],
    queryFn: fetchData,
    retry: false,
    placeholderData: keepPreviousData,
  });

  return {
    query,
    filter,
    setFilter,
    sort,
    setSort,
    pagination,
    setPagination,
  };
}
